import React, { useState, useContext } from "react";
import {
  Avatar,
  Box,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  Select,
  Typography,
  FormControl,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../../../context/AuthContext/AuthContext";
import { usePermissions } from "../../../../context/PermissionsContext/PermissionsContext";
import { useViewAsRole } from "../../../../context/ViewAsRoleContext/ViewAsRoleContext";
import { UserRoleName } from "../../../../themes/Enum";
import useAuthUser from "../../../../hooks/useAuthUser";

const UserMenu = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { signOut } = useContext(AuthContext);
  const { isAdmin } = usePermissions();
  const { viewAsRole, viewAsRoleLabel, switchRole, resetRole, availableRoles } =
    useViewAsRole();

  const userData = useAuthUser() || {};
  const userName = userData.name || userData.fullName || "User";
  const userRole = UserRoleName[userData.rol] || "User";
  const userLocation = userData.location || "";
  const userInitial = userName.charAt(0).toUpperCase();

  const open = Boolean(anchorEl);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    signOut();
    navigate("/login", { replace: true });
  };

  const handleProfile = () => {
    handleClose();
    navigate("/profile");
  };

  const handleRoleChange = (event) => {
    switchRole(event.target.value || null);
  };

  return (
    <>
      <IconButton onClick={handleOpen} sx={{ p: 0.5 }}>
        <Avatar
          sx={{
            width: 36,
            height: 36,
            background: "linear-gradient(135deg, #00bcd4 0%, #0C343D 100%)",
            fontSize: "0.95rem",
            fontWeight: 600,
          }}
        >
          {userInitial}
        </Avatar>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: { width: 280, mt: 1, borderRadius: 2 },
          },
        }}
      >
        {/* Header con info del usuario */}
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              mx: "auto",
              mb: 1,
              background: "linear-gradient(135deg, #00bcd4 0%, #0C343D 100%)",
              fontSize: "1.5rem",
              fontWeight: 600,
            }}
          >
            {userInitial}
          </Avatar>
          <Typography variant="subtitle1" fontWeight={600}>
            {userName}
          </Typography>
          <Chip
            label={userRole}
            size="small"
            sx={{ mt: 0.5, fontSize: "0.75rem" }}
          />
          {userLocation && (
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
              {userLocation}
            </Typography>
          )}
        </Box>

        <Divider />

        {/* View as Role - Solo Admin */}
        {isAdmin && (
          <>
            <Box sx={{ px: 2, py: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <VisibilityIcon fontSize="small" color="action" />
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  {t("nav.viewAsRole", "View as Role")}
                </Typography>
              </Box>
              <FormControl fullWidth size="small">
                <Select
                  value={viewAsRole || ""}
                  onChange={handleRoleChange}
                  displayEmpty
                  sx={{ fontSize: "0.85rem" }}
                >
                  <MenuItem value="">
                    <em>Admin (default)</em>
                  </MenuItem>
                  {Object.entries(availableRoles).map(([roleKey, roleLabel]) => (
                    <MenuItem key={roleKey} value={roleKey}>
                      {roleLabel}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {viewAsRole && (
                <Chip
                  label={`${t("nav.viewingAs", "Viewing as")}: ${viewAsRoleLabel}`}
                  size="small"
                  color="info"
                  onDelete={resetRole}
                  deleteIcon={<RestartAltIcon />}
                  sx={{ mt: 1, width: "100%" }}
                />
              )}
            </Box>
            <Divider />
          </>
        )}

        {/* Menu items */}
        <MenuList dense>
          <MenuItem onClick={handleProfile}>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t("nav.myProfile", "My Profile")}</ListItemText>
          </MenuItem>
          <MenuItem disabled>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t("nav.settings", "Settings")}</ListItemText>
          </MenuItem>
        </MenuList>

        <Divider />

        <MenuList dense>
          <MenuItem
            onClick={handleLogout}
            sx={{
              "&:hover": {
                backgroundColor: "rgba(244,67,54,0.08)",
                "& .MuiListItemIcon-root": { color: "#f44336" },
                "& .MuiListItemText-primary": { color: "#f44336" },
              },
            }}
          >
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t("nav.logOut", "Log Out")}</ListItemText>
          </MenuItem>
        </MenuList>
      </Popover>
    </>
  );
};

export default UserMenu;
